import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import axiosInstance from "@/api/config";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import NotificationContent from "@/components/notification/notificationContent";
import { Button } from "@/components/ui/button";
import { STATUS_CODE } from "@/constants/errorCode";
import { MESSAGE } from "@/constants/message";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { RootState } from "@/store";
import {
  addQueuedJobId,
  addUnOpenedJobIds,
  removeQueuedJobId,
  upsertAnalysisJob
} from "@/store/slice/analysisJob";
import {
  clearCurrRunningAnalysisId,
  setCurrRunningAnalysisId,
  setLatestAnalysisId
} from "@/store/slice/spatialSlice";
import { type AnalysisJobItem, AnalysisJobStatus } from "@/types/analysis";

import { useAppFeatures } from "./useAppFeatures";
import useEventSource from "./useEventSource";

import { useAuth } from "./auth/useAuth";
import useToast, { EToastType } from "./ui/useToast";

export type AnalysisJobStreamEventHandlers = {
  "analysis-list": string; // The event data is expected to be a string of JSON stringified array of AnalysisJobItem
};

const useAnalysisJob = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const pathname = location.pathname;
  const { data: userProfileData } = useGetUserProfileAPI({
    enabled: isAuthenticated
  });
  const { isAnalysisJobListEnabled } = useAppFeatures();

  const queuedJobIds = useSelector(
    (state: RootState) => state.analysisJob.queuedJobIds
  );

  const handleQueuedJob = (id: string) => {
    dispatch(clearCurrRunningAnalysisId()); // Prevent getAnalysis on useAnalysisResult being triggered when the success(queued) toast is closed
    dispatch(addQueuedJobId(id));
  };

  const handleSuccessJob = (id: string) => {
    dispatch(addUnOpenedJobIds(id));

    if (
      queuedJobIds.includes(id) ||
      (pathname !== "/" && pathname !== "/login")
    ) {
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent description="Your analysis is finished. Click below to view the results.">
            <Button
              variant="link"
              className="p-0 text-[#4C7DFB]"
              onClick={() => {
                // open analysis report
                dispatch(removeQueuedJobId(id));
                dispatch(setCurrRunningAnalysisId(id));
                dispatch(setLatestAnalysisId(id));
                // navigate to home if not already on home page
                if (pathname !== "/") {
                  navigate("/");
                }
              }}
            >
              View analysis report
            </Button>
          </NotificationContent>
        ),
        autoClose: false,
        onClose: () => {
          dispatch(removeQueuedJobId(id));
        }
      });
    }
  };

  const handleGetDataAnalysisJob = (dataString: string) => {
    const data: AnalysisJobItem[] = JSON.parse(dataString);
    // Make sure the data conforms to the expected structure and contains exactly one item
    if (!Array.isArray(data)) return;

    dispatch(upsertAnalysisJob(data));

    const job = data[0];
    const { status, created_by, id } = job;

    // check if the job is created by the current user
    const isValidUser =
      created_by.toLowerCase() === userProfileData?.full_name?.toLowerCase();

    if (!isValidUser) return;

    // if created by the current user, and the status is QUEUED, add to queuedJobIds
    if (status === AnalysisJobStatus.QUEUED) {
      handleQueuedJob(id);
    }

    // if created by the current user, and the status is SUCCESS, add to unOpenedJobIds
    if (status === AnalysisJobStatus.SUCCESS) {
      handleSuccessJob(id);
    }
  };

  useEventSource<AnalysisJobStreamEventHandlers>({
    url: `${axiosInstance.defaults.baseURL}/v1/analysis`,
    eventKey: "analysis-list",
    eventHandlers: {
      "analysis-list": handleGetDataAnalysisJob
    },
    enabled: isAnalysisJobListEnabled && !!userProfileData,
    onError: (error) => {
      const { code, reason } = error;
      // Handle specific error codes or reasons to provide more informative feedback to the user
      // For example, if the service is unavailable, we can show a specific message indicating that the analysis job stream is currently unavailable.
      if (code === STATUS_CODE.SERVICE_UNAVAILABLE) {
        showToast({
          message: MESSAGE.SERVICE_UNAVAILABLE,
          type: EToastType.ERROR,
          autoClose: false
        });
      } else if (reason === "ERR_AUTH_PERMISSION_DENIED") {
        // This error might indicate that the user's authentication token has expired or is invalid or not have permission, leading to permission issues when trying to access the analysis job stream. Prompting the user to re-authenticate can help resolve this issue.
        showToast({
          message: getDisplayErrorMessage(error),
          type: EToastType.ERROR,
          autoClose: false
        });
      }
    }
  });
};

export default useAnalysisJob;
